import { Fragment, useEffect, useState } from 'react';
import { matchPath, useLocation } from 'react-router-dom';

// material-ui
import { useTheme } from '@mui/material/styles';
import useMediaQuery from '@mui/material/useMediaQuery';
import ClickAwayListener from '@mui/material/ClickAwayListener';
import Divider from '@mui/material/Divider';
import List from '@mui/material/List';
import ListItemButton from '@mui/material/ListItemButton';
import ListItemIcon from '@mui/material/ListItemIcon';
import ListItemText from '@mui/material/ListItemText';
import Paper from '@mui/material/Paper';
import Popper from '@mui/material/Popper';
import Typography from '@mui/material/Typography';
import ListSubheader from '@mui/material/ListSubheader';
import Box from '@mui/material/Box';
import Tooltip from '@mui/material/Tooltip';

// third party
import { FormattedMessage } from 'react-intl';

// project imports
import NavCollapse from '../NavCollapse';
import NavItem from '../NavItem';
import { MenuOrientation } from 'config';
import useConfig from 'hooks/useConfig';
import Transitions from 'ui-component/extended/Transitions';
import { useGetMenuMaster } from 'api/menu';

// assets
import { IconChevronDown, IconChevronRight, IconMinusVertical } from '@tabler/icons-react';

// types
import { NavItemType } from 'types';
import useLocalization from 'hooks/useLocalization';
import { clickPreventDefaultAndStopPropagation, getNestedValue } from 'utils/helpers';
import { getPathnameOnly } from 'utils/chat-workflow-url';
import { IconButton, Stack } from '@mui/material';
import { ellipsisTextOverflowStyles } from 'themes/common/typography';
import AdditionalViews, { type AdditionalViewsHandle } from '../NavCollapse/AdditionalViews';
import { ViewsPagesVisibilityProvider } from '../ViewsPagesVisibilityContext';
import ChatViewsAllViewsMenu from '../../Sidebar/ChatViewsAllViewsMenu';
import ChatViewsScopeAccordions from '../../Sidebar/ChatViewsScopeAccordions';
import ChatSidebarFolderDialog, { type FolderDialogMode } from '../../Sidebar/ChatSidebarFolderDialog';
import ManageChatSidebarViewsDialog from '../../Sidebar/ManageChatSidebarViewsDialog';
import RecentChatsHistoryButton from '../../LogoSection/RecentChatsHistoryButton';
import { useChatSidebarLayoutOptional } from 'contexts/ChatSidebarLayoutContext';

type VirtualElement = {
  getBoundingClientRect: () => DOMRectReadOnly | DOMRect;
  contextElement?: Element;
};

interface NavGroupProps {
  item: NavItemType;
  lastItem: number;
  remItems: NavItemType[];
  lastItemId: string;
  setSelectedID: React.Dispatch<React.SetStateAction<string | undefined>>;
  selectedID: string | undefined;
  additionalViewsRef: React.RefObject<AdditionalViewsHandle | null>;
  createEditView: 'create' | 'edit' | null;
  setCreateEditView: React.Dispatch<React.SetStateAction<'create' | 'edit' | null>>;
}

// ==============================|| SIDEBAR MENU LIST GROUP ||============================== //

export default function NavGroup({
  item,
  lastItem,
  remItems,
  lastItemId,
  selectedID,
  setSelectedID,
  additionalViewsRef,
  createEditView,
  setCreateEditView,
}: NavGroupProps) {
  const theme = useTheme();
  const { currentLocale } = useLocalization();
  const downMD = useMediaQuery(theme.breakpoints.down('md'));
  const { pathname } = useLocation();

  const { menuOrientation, borderRadius } = useConfig();
  const { menuMaster } = useGetMenuMaster();
  const drawerOpen = menuMaster.isDashboardDrawerOpened;
  const isHorizontal = menuOrientation === MenuOrientation.HORIZONTAL && !downMD;

  const [anchorEl, setAnchorEl] = useState<VirtualElement | (() => VirtualElement) | null | undefined>(null);
  const [currentItem, setCurrentItem] = useState(item);
  const [viewsHeaderHovered, setViewsHeaderHovered] = useState(false);
  const chatSidebarLayout = useChatSidebarLayoutOptional();

  // דיאלוג יצירה/שינוי-שם של תיקייה (אחסון מקומי בלבד — TODO(DB))
  const [folderDialog, setFolderDialog] = useState<{
    open: boolean;
    mode: FolderDialogMode;
    folderId: string | null;
    name: string;
  }>({ open: false, mode: 'create', folderId: null, name: '' });

  const openCreateFolder = () =>
    setFolderDialog({ open: true, mode: 'create', folderId: null, name: '' });
  const openRenameFolder = (folderId: string, currentName: string) =>
    setFolderDialog({ open: true, mode: 'rename', folderId, name: currentName });
  const closeFolderDialog = () => setFolderDialog((prev) => ({ ...prev, open: false }));
  const submitFolderDialog = (name: string) => {
    if (folderDialog.mode === 'create') {
      chatSidebarLayout?.addFolder(name);
    } else if (folderDialog.folderId) {
      chatSidebarLayout?.renameFolder(folderDialog.folderId, name);
    }
  };

  const openMini = Boolean(anchorEl);

  useEffect(() => {
    if (lastItem) {
      if (item.id === lastItemId) {
        const localItem: any = { ...item };
        const elements = remItems.map((ele: NavItemType) => ele.elements);
        localItem.children = elements.flat(1);
        setCurrentItem(localItem);
      } else {
        setCurrentItem(item);
      }
    }
  }, [item, lastItem, menuOrientation, remItems, lastItemId]);

  const checkOpenForParent = (child: NavItemType[], id: string) => {
    child.forEach((ele: NavItemType) => {
      if (ele.type === 'section') return;
      if (ele.children?.length) {
        checkOpenForParent(ele.children, currentItem.id!);
      }
      const pathOnly = ele?.url ? getPathnameOnly(ele.link ? ele.link : ele.url) : '';
      if (ele?.url && !!matchPath({ path: pathOnly, end: false }, pathname)) {
        setSelectedID(id);
      }
    });
  };

  const checkSelectedOnload = (data: NavItemType) => {
    const childrens = data.children ? data.children : [];
    childrens.forEach((itemCheck: NavItemType) => {
      if (itemCheck.type === 'section') return;
      if (itemCheck?.children?.length) {
        checkOpenForParent(itemCheck.children, currentItem.id!);
      }
      const pathOnly = itemCheck?.url ? getPathnameOnly(itemCheck?.link ? itemCheck.link : itemCheck.url) : '';
      if (itemCheck?.url && !!matchPath({ path: pathOnly, end: false }, pathname)) {
        setSelectedID(currentItem.id!);
      }
    });

    if (data?.url) {
      const pathOnly = getPathnameOnly(data?.link ? data.link : data.url!);
      if (!!matchPath({ path: pathOnly, end: false }, pathname)) {
        setSelectedID(currentItem.id!);
      }
    }
  };

  // keep selected-menu on page load and use for horizontal menu close on change routes
  useEffect(() => {
    checkSelectedOnload(currentItem);
    if (openMini) setAnchorEl(null);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pathname, currentItem]);

  const handleClick = (
    event: React.MouseEvent<HTMLAnchorElement> | React.MouseEvent<HTMLDivElement, MouseEvent> | undefined,
  ) => {
    if (!openMini) {
      setAnchorEl(event?.currentTarget);
    }
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const Icon = currentItem?.icon!;
  const itemIcon = currentItem?.icon ? <Icon stroke={1.5} size="24px" /> : null;

  // menu list collapse & items
  const items = currentItem.children?.map((menu) => {
    switch (menu?.type) {
      case 'section':
        return (
          <ListSubheader
            key={menu.id}
            disableSticky
            sx={{
              typography: 'caption',
              fontWeight: 700,
              lineHeight: 1.4,
              py: 1,
              px: drawerOpen ? 2 : 1,
              mt: 0.5,
              color:
                menu.navGroup === 'inbox'
                  ? 'warning.dark'
                  : menu.navGroup === 'ongoing'
                    ? 'text.secondary'
                    : menu.navGroup === 'monitoring'
                      ? 'text.secondary'
                      : 'text.secondary',
            }}
          >
            {menu?.customPage && menu.title ? (
              getNestedValue(currentLocale, menu.title)
            ) : (
              <FormattedMessage id={menu.title!} />
            )}
          </ListSubheader>
        );
      case 'collapse':
        return <NavCollapse key={menu.id} menu={menu} level={1} parentId={currentItem.id!} />;
      case 'item':
        return <NavItem key={menu.id} item={menu} level={1} />;
      default:
        return (
          <Typography key={menu?.id} variant="h6" color="error" align="center">
            Menu Items Error
          </Typography>
        );
    }
  });

  const moreItems = remItems.map((itemRem: NavItemType, i) => (
    <Fragment key={i}>
      {itemRem.url ? (
        <NavItem item={itemRem} level={1} />
      ) : (
        itemRem.title && (
          <Typography variant="caption" sx={{ pl: 2 }}>
            {itemRem.title} {itemRem.url}
          </Typography>
        )
      )}
      {itemRem?.elements?.map((menu) => {
        switch (menu?.type) {
          case 'collapse':
            return <NavCollapse key={menu.id} menu={menu} level={1} parentId={currentItem.id!} />;
          case 'item':
            return <NavItem key={menu.id} item={menu} level={1} />;
          default:
            return (
              <Typography key={menu.id} variant="h6" color="error" align="center">
                Menu Items Error
              </Typography>
            );
        }
      })}
    </Fragment>
  ));

  const popperId = openMini ? `group-pop-${item.id}` : undefined;
  const isSelected = selectedID === currentItem.id;

  //* custom views
  const viewsPagesTitleSx = {
    fontSize: '1.2rem',
    fontWeight: 700,
    lineHeight: 1.3,
    letterSpacing: '-0.01em',
    color: theme.palette.mode === 'dark' ? theme.palette.grey[100] : '#1f2937',
  } as const;

  const viewsPagesBorderColor =
    theme.palette.mode === 'dark' ? 'rgba(255, 255, 255, 0.08)' : 'rgba(0, 0, 0, 0.06)';

  const viewsPagesSubheader =
    item.id === 'views-pages' && currentItem.title ? (
      drawerOpen ? (
        <Box
          component="div"
          sx={{
            display: 'block',
            width: '100%',
            pt: 0,
            mt: 0,
            pb: 1,
            borderBottom: '1px solid',
            borderColor: viewsPagesBorderColor,
            transition: theme.transitions.create(['border-color'], { duration: 200 }),
          }}
        >
          <Stack
            direction="row"
            spacing={0.5}
            alignItems="center"
            sx={{ minHeight: 36 }}
            onMouseEnter={() => setViewsHeaderHovered(true)}
            onMouseLeave={() => setViewsHeaderHovered(false)}
          >
            <Box
              sx={{
                flex: '1 1 auto',
                minWidth: 0,
                ...ellipsisTextOverflowStyles,
              }}
            >
              {currentItem?.customPage && currentItem.title ? (
                <Typography component="div" sx={viewsPagesTitleSx}>
                  {getNestedValue(currentLocale, currentItem.title)}
                </Typography>
              ) : (
                <Typography component="div" sx={viewsPagesTitleSx}>
                  <FormattedMessage id={currentItem.title} />
                </Typography>
              )}

              {currentItem.caption && (
                <Typography
                  variant="caption"
                  gutterBottom
                  sx={{ display: 'block', mt: 0.25, ...theme.typography.subMenuCaption }}
                >
                  {currentItem?.customPage && currentItem.caption ? (
                    getNestedValue(currentLocale, currentItem.caption)
                  ) : (
                    <FormattedMessage id={currentItem.caption} />
                  )}
                </Typography>
              )}
            </Box>
            <RecentChatsHistoryButton />
            {chatSidebarLayout && (
              <ChatViewsAllViewsMenu headerHovered={viewsHeaderHovered} onCreateFolderClick={openCreateFolder} />
            )}
          </Stack>
        </Box>
      ) : (
        <Box
          component="div"
          sx={{
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            width: '100%',
            pt: 0.5,
            pb: 1,
            borderBottom: '1px solid',
            borderColor: viewsPagesBorderColor,
          }}
        >
          <RecentChatsHistoryButton compact />
        </Box>
      )
    ) : null;

  const verticalDrawerListSubheader =
    currentItem.title &&
    drawerOpen &&
    item.id !== 'views-pages' ? (
      <Typography
        variant="caption"
        gutterBottom
        sx={{
          display: 'block',
          ...theme.typography.menuCaption,
          ...(item.id === 'sidebar-pages' && { mt: '24px' }),
        }}
      >
        <Stack direction="row" spacing={1} alignItems={'center'}>
          <Box
            sx={{
              flex: '1 1 auto',
              ...ellipsisTextOverflowStyles,
            }}
          >
            {currentItem?.customPage && currentItem.title ? (
              getNestedValue(currentLocale, currentItem.title)
            ) : (
              <FormattedMessage id={currentItem.title} />
            )}

            {currentItem.caption && (
              <Typography
                variant="caption"
                gutterBottom
                sx={{ display: 'block', ...theme.typography.subMenuCaption }}
              >
                {currentItem?.customPage && currentItem.caption ? (
                  getNestedValue(currentLocale, currentItem.caption)
                ) : (
                  <FormattedMessage id={currentItem.caption} />
                )}
              </Typography>
            )}
          </Box>
        </Stack>
      </Typography>
    ) : null;

  return (
    <>
      {!isHorizontal ? (
        <>
          {item.id === 'views-pages' ? (
            <>
              <List
                disablePadding={!drawerOpen}
                subheader={viewsPagesSubheader}
                sx={{
                  flexShrink: 0,
                  minHeight: drawerOpen ? 160 : 44,
                  /** מכיל את z-index גבוה ב־NavItem (1201) — בלי זה שורות «שיחות» מצוירות מעל בלוק «תצוגות מותאמות» ומסתירות אותו */
                  position: 'relative',
                  zIndex: 0,
                  isolation: 'isolate',
                  '& > .MuiListSubheader-root': {
                    pt: 0,
                    pb: 0,
                    lineHeight: 1.2,
                  },
                }}
              >
                <Box
                  className="views-scroll"
                  sx={{
                    width: '100%',
                    minWidth: 0,
                    boxSizing: 'border-box',
                    pt: 0,
                  }}
                >
                  <ViewsPagesVisibilityProvider>
                    <ChatViewsScopeAccordions
                      additionalViewsRef={additionalViewsRef}
                      onCreateFolder={openCreateFolder}
                      onRenameFolder={openRenameFolder}
                    />
                    <AdditionalViews
                      ref={additionalViewsRef as React.RefObject<AdditionalViewsHandle>}
                      createEditView={createEditView}
                      setCreateEditView={setCreateEditView}
                      suppressInlineList
                    />
                    <ManageChatSidebarViewsDialog />
                    <ChatSidebarFolderDialog
                      open={folderDialog.open}
                      mode={folderDialog.mode}
                      initialName={folderDialog.name}
                      onClose={closeFolderDialog}
                      onSubmit={submitFolderDialog}
                    />
                  </ViewsPagesVisibilityProvider>
                </Box>
              </List>
            </>
          ) : (
            <Box
              sx={
                item.id === 'sidebar-pages'
                  ? {
                      position: 'sticky',
                      bottom: 0,
                      /** מעל שכבת רשימת השיחות (אחרי isolate); לא מתחרה ב־NavItem 1201 בתוך אותה רשימה */
                      zIndex: 5,
                      flexShrink: 0,
                      bgcolor: 'background.default',
                      pt: 0.5,
                    }
                  : undefined
              }
            >
              <List
                disablePadding={!drawerOpen}
                subheader={item.id === 'sidebar-pages' ? undefined : verticalDrawerListSubheader}
                sx={
                  {
                    // overflow: 'hidden',
                    // maxHeight: item.id === 'views-pages' ? 400 : undefined,
                    // overflowY: item.id === 'views-pages' ? 'auto' : undefined,
                  }
                }
              >
                {items}
              </List>
            </Box>
          )}
        </>
      ) : (
        <List>
          <ListItemButton
            selected={isSelected}
            sx={{
              borderRadius: `${borderRadius}px`,
              p: 1,
              my: 0.5,
              mr: 1,
              display: 'flex',
              alignItems: 'center',
              backgroundColor: 'inherit',
            }}
            onMouseEnter={handleClick}
            onClick={handleClick}
            onMouseLeave={handleClose}
            aria-describedby={popperId}
          >
            {itemIcon && (
              <ListItemIcon sx={{ minWidth: 36 }}>
                {currentItem.id === lastItemId ? <IconMinusVertical stroke={1.5} size="20px" /> : itemIcon}
              </ListItemIcon>
            )}
            <ListItemText
              sx={{ mr: 1, mb: 0.25 }}
              primary={
                <Typography variant={isSelected ? 'h5' : 'body1'} color="inherit">
                  {currentItem.id === lastItemId ? <FormattedMessage id="more-items" /> : currentItem.title}
                </Typography>
              }
            />
            {openMini ? (
              <IconChevronDown stroke={1.5} size="16px" />
            ) : (
              <IconChevronRight stroke={1.5} size="16px" />
            )}

            {anchorEl && (
              <Popper
                id={popperId}
                open={openMini}
                anchorEl={anchorEl}
                placement="bottom-start"
                sx={{
                  overflow: 'visible',
                  zIndex: 2001,
                  minWidth: 180,
                  '&:before': {
                    content: '""',
                    display: 'block',
                    position: 'absolute',
                    top: 5,
                    left: 32,
                    width: 12,
                    height: 12,
                    transform: 'translateY(-50%) rotate(45deg)',
                    zIndex: 120,
                    borderWidth: '6px',
                    borderStyle: 'solid',
                    borderTopColor: 'background.paper',
                    borderLeftColor: 'background.paper',
                    borderRightColor: 'transparent',
                    borderBottomColor: 'transparent',
                  },
                }}
              >
                {({ TransitionProps }) => (
                  <Transitions in={openMini} {...TransitionProps}>
                    <Paper
                      sx={{
                        mt: 0.5,
                        py: 1.25,
                        boxShadow: theme.shadows[8],
                        backgroundImage: 'none',
                      }}
                    >
                      <ClickAwayListener onClickAway={handleClose}>
                        <Box
                          sx={{
                            minWidth: 200,
                            maxHeight: 'calc(100vh - 170px)',
                            overflowY: 'auto',
                            '&::-webkit-scrollbar': {
                              opacity: 0,
                              width: 4,
                              '&:hover': {
                                opacity: 0.7,
                              },
                            },
                            '&::-webkit-scrollbar-track': {
                              bgcolor: 'transparent',
                            },
                            '&::-webkit-scrollbar-thumb': {
                              bgcolor: 'divider',
                              borderRadius: 4,
                            },
                          }}
                        >
                          {currentItem.id !== lastItemId ? items : moreItems}
                        </Box>
                      </ClickAwayListener>
                    </Paper>
                  </Transitions>
                )}
              </Popper>
            )}
          </ListItemButton>
        </List>
      )}
    </>
  );
}
